import React, { useState } from 'react';
import { FileTreeNode } from '@/types';
import { getFileIcon } from '@/utils/fileUtils';
import { ChevronRight, ChevronDown, Plus, FolderPlus, Upload } from 'lucide-react';

interface FileTreeProps {
  tree: FileTreeNode[];
  onFileSelect: (path: string) => void;
  onFileCreate: (parentPath: string, isFolder: boolean) => void;
  onFileUpload: (parentPath: string) => void;
  selectedPath: string | null;
  onTreeUpdate: (newTree: FileTreeNode[]) => void;
}

const FileTree: React.FC<FileTreeProps> = ({
  tree,
  onFileSelect,
  onFileCreate,
  onFileUpload,
  selectedPath,
  onTreeUpdate,
}) => {
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    node: FileTreeNode;
  } | null>(null);

  const toggleFolder = (path: string) => {
    const updateTree = (nodes: FileTreeNode[]): FileTreeNode[] => {
      return nodes.map(node => {
        if (node.path === path && node.type === 'folder') {
          return { ...node, expanded: !node.expanded };
        }
        if (node.children) {
          return { ...node, children: updateTree(node.children) };
        }
        return node;
      });
    };
    
    onTreeUpdate(updateTree(tree));
  };

  const handleContextMenu = (e: React.MouseEvent, node: FileTreeNode) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      node,
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const handleContextAction = (action: string) => {
    if (!contextMenu) return;
    
    const { node } = contextMenu;
    const parentPath = node.type === 'folder' ? node.path : node.path.split('/').slice(0, -1).join('/');
    
    switch (action) {
      case 'new-file':
        onFileCreate(parentPath, false);
        break;
      case 'new-folder':
        onFileCreate(parentPath, true);
        break;
      case 'upload':
        onFileUpload(parentPath);
        break;
    }
    
    closeContextMenu();
  };

  const renderNode = (node: FileTreeNode, depth: number = 0) => {
    const isSelected = selectedPath === node.path;
    const paddingLeft = depth * 16 + 8;

    return (
      <div key={node.path}>
        <div
          className={`file-tree-item ${isSelected ? 'selected' : ''}`}
          style={{ paddingLeft: `${paddingLeft}px` }}
          onClick={() => {
            if (node.type === 'folder') {
              toggleFolder(node.path);
            } else {
              onFileSelect(node.path);
            }
          }}
          onContextMenu={(e) => handleContextMenu(e, node)}
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {node.type === 'folder' && (
              <span className="text-gray-400 flex-shrink-0">
                {node.expanded ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </span>
            )}
            <span className="flex-shrink-0">
              {getFileIcon(node.name, node.type === 'folder')}
            </span>
            <span className="truncate text-sm">
              {node.name}
            </span>
          </div>
        </div>
        
        {node.type === 'folder' && node.expanded && node.children && (
          <div>
            {node.children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="file-tree h-full bg-sidebar-bg text-white">
      {/* Header */}
      <div className="p-3 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Files</h3>
          <div className="flex space-x-1">
            <button
              onClick={() => onFileCreate('', false)}
              className="p-1 hover:bg-gray-700 rounded"
              title="New file"
            >
              <Plus size={14} />
            </button>
            <button
              onClick={() => onFileCreate('', true)}
              className="p-1 hover:bg-gray-700 rounded"
              title="New folder"
            >
              <FolderPlus size={14} />
            </button>
            <button
              onClick={() => onFileUpload('')}
              className="p-1 hover:bg-gray-700 rounded"
              title="Upload file"
            >
              <Upload size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-auto">
        {tree.length === 0 ? (
          <div className="p-4 text-center text-gray-400 text-sm">
            <p>No files yet</p>
            <p className="mt-1">Right-click to create files</p>
          </div>
        ) : (
          <div>
            {tree.map(node => renderNode(node))}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={closeContextMenu}
          />
          <div
            className="context-menu"
            style={{
              left: contextMenu.x,
              top: contextMenu.y,
            }}
          >
            <div
              className="context-menu-item"
              onClick={() => handleContextAction('new-file')}
            >
              <Plus size={14} className="mr-2" />
              New File
            </div>
            <div
              className="context-menu-item"
              onClick={() => handleContextAction('new-folder')}
            >
              <FolderPlus size={14} className="mr-2" />
              New Folder
            </div>
            <div className="context-menu-separator" />
            <div
              className="context-menu-item"
              onClick={() => handleContextAction('upload')}
            >
              <Upload size={14} className="mr-2" />
              Upload File
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FileTree;